import { CancelTokenSource } from 'axios';

import { BundleUploadStage } from '../serverApi/types/IBundleUpload';
import { IExecution } from '../serverApi/types/IExecution';
import {
  beginBundleUpload,
  cancelBundleUpload,
  createCancelToken,
  getUploadExecutionStatus,
  isTerminalStage,
  toUploadStage,
  triggerBundleUpload,
  uploadBundleFile,
  UploadBundleFileOptions,
} from './bundleUploadActions';

/**
 * The upload dialog's phases:
 *
 *   pick -> uploading (browser sends the chunks) -> triggering (queue the upload task)
 *        -> tracking (poll the execution) -> ready | invalidBundle | cancelled
 *
 * plus cancelling (waiting for the server to confirm a cancel), uploadFailed (couldn't reserve,
 * upload or trigger) and statusUnavailable (lost track of a triggered upload — it may still finish).
 */
export type BundleUploadPhase =
  | 'pick'
  | 'uploading'
  | 'triggering'
  | 'tracking'
  | 'cancelling'
  | 'ready'
  | 'invalidBundle'
  | 'uploadFailed'
  | 'statusUnavailable'
  | 'cancelled';

export interface BundleUploadState {
  phase: BundleUploadPhase;
  /** Browser-side transfer progress, 0-100, while uploading. */
  uploadPercent: number;
  /** The latest polled execution while tracking. */
  execution?: IExecution;
}

/** The server calls the controller drives; injectable so the attempt logic can be unit tested. */
export interface BundleUploadApi {
  begin: () => Promise<string>;
  uploadFile: (file: File, options: UploadBundleFileOptions) => Promise<string>;
  trigger: (uploadSessionId: string, bundleLuid: string, filename?: string) => Promise<number>;
  getStatus: (backgroundJobId: number) => Promise<IExecution>;
  cancel: (bundleLuid: string, backgroundJobId?: number) => Promise<IExecution>;
}

export type BundleUploadErrorKind = 'upload' | 'cancel';

export const INITIAL_BUNDLE_UPLOAD_STATE: BundleUploadState = { phase: 'pick', uploadPercent: 0 };

/** How often to poll the execution while the service validates and unpacks the bundle. */
export const POLL_INTERVAL_MS = 2000;
/** Ceiling for the backoff between polls after transient status failures. */
export const MAX_POLL_BACKOFF_MS = 30000;
/** Consecutive status failures tolerated before giving up on tracking. */
export const MAX_CONSECUTIVE_POLL_FAILURES = 5;
/** Longest the dialog tracks one upload (e.g. a job that never gets linked) before giving up. */
export const MAX_TRACKING_MS = 30 * 60 * 1000;

const DEFAULT_API: BundleUploadApi = {
  begin: beginBundleUpload,
  uploadFile: uploadBundleFile,
  trigger: triggerBundleUpload,
  getStatus: getUploadExecutionStatus,
  cancel: cancelBundleUpload,
};

const TERMINAL_PHASES: { [stage: string]: BundleUploadPhase } = {
  ready: 'ready',
  failed: 'invalidBundle',
  cancelled: 'cancelled',
};

/** Thrown inside an attempt's chain once it has been abandoned, to stop its remaining steps. */
const ABANDONED = new Error('Upload attempt abandoned');

/** One click of Upload. Every step reads and writes its own attempt, never another attempt's state. */
interface Attempt {
  cancelToken: CancelTokenSource;
  /** Cancelled (or the dialog closed) before a job was tracked; the chain cleans up when it settles. */
  abandoned: boolean;
  /** The reservation / job has been released, so cleanup never runs twice. */
  released: boolean;
  bundleLuid?: string;
  backgroundJobId?: number;
  pollTimer?: number;
  pollFailures: number;
  trackingStartedAtMs?: number;
}

/**
 * Drives one upload at a time through reserve -> chunked upload -> trigger -> poll, and owns the
 * cancel / retry / close cases so they hold up against slow or out-of-order server responses:
 *
 * - Each Upload click is its own {@link Attempt}. A step only updates the dialog while its attempt is
 *   the current, live one, so a stale attempt can't overwrite a newer one or start a second poll loop.
 * - Cancelling before a job is tracked abandons the attempt immediately (back to the picker); the
 *   attempt's chain then releases its reservation, or cancels the job by id if the trigger landed
 *   after the cancel.
 * - Cancelling a tracked upload waits for the server; if the cancel fails (e.g. the job finished
 *   first), the real state is re-polled and shown instead.
 * - A transient status failure is retried with backoff; only repeated failures (or a very long wait)
 *   end tracking, as "status unavailable" rather than as a damaged package.
 */
export class BundleUploadController {
  private state: BundleUploadState = INITIAL_BUNDLE_UPLOAD_STATE;
  private attempt: Attempt | undefined;
  private disposed = false;

  constructor(
    private readonly onChange: (state: BundleUploadState) => void,
    private readonly onError: (kind: BundleUploadErrorKind) => void,
    private readonly api: BundleUploadApi = DEFAULT_API
  ) {}

  public getState(): BundleUploadState {
    return this.state;
  }

  /** Starts uploading `file`. Ignored while another upload is in progress. */
  public start(file: File): void {
    if (this.disposed || this.isBusy()) {
      return;
    }
    const attempt: Attempt = { cancelToken: createCancelToken(), abandoned: false, released: false, pollFailures: 0 };
    this.attempt = attempt;
    this.setState({ phase: 'uploading', uploadPercent: 0, execution: undefined });

    const self = this;
    this.api.begin()
      .then(function(bundleLuid) {
        attempt.bundleLuid = bundleLuid;
        throwIfAbandoned(attempt);
        return self.api.uploadFile(file, {
          cancelToken: attempt.cancelToken,
          onProgress: function(percent) {
            if (self.isLive(attempt)) {
              self.setState({ uploadPercent: percent });
            }
          },
        });
      })
      .then(function(uploadSessionId) {
        throwIfAbandoned(attempt);
        self.setState({ phase: 'triggering' });
        return self.api.trigger(uploadSessionId, attempt.bundleLuid, file.name);
      })
      .then(function(backgroundJobId) {
        attempt.backgroundJobId = backgroundJobId;
        throwIfAbandoned(attempt);
        attempt.trackingStartedAtMs = Date.now();
        self.setState({ phase: 'tracking' });
        self.poll(attempt);
      })
      .catch(function() {
        // Whatever stopped the chain, release what this attempt holds on the server.
        self.release(attempt);
        if (!attempt.abandoned && self.isLive(attempt)) {
          self.setState({ phase: 'uploadFailed' });
          self.onError('upload');
        }
      });
  }

  /** Cancels the current upload, if one is in progress. */
  public cancel(): void {
    const attempt = this.attempt;
    if (!attempt || this.disposed) {
      return;
    }
    const phase = this.state.phase;
    if (phase === 'uploading' || phase === 'triggering') {
      abandon(attempt);
      this.setState({ phase: 'pick', uploadPercent: 0 });
    } else if (phase === 'tracking') {
      this.cancelTracked(attempt);
    }
  }

  /** Returns to the picker from an end state (Try again). */
  public reset(): void {
    if (this.disposed || this.isBusy()) {
      return;
    }
    if (this.attempt) {
      stopPolling(this.attempt);
    }
    this.attempt = undefined;
    this.setState(INITIAL_BUNDLE_UPLOAD_STATE);
  }

  /**
   * The dialog closed. Before a job is tracked this abandons the upload (its chain releases the
   * reservation or cancels a just-triggered job); once tracked, the upload keeps running server-side
   * and only the client poll stops.
   */
  public dispose(): void {
    const attempt = this.attempt;
    const phase = this.state.phase;
    this.disposed = true;
    if (!attempt) {
      return;
    }
    stopPolling(attempt);
    if (phase === 'uploading' || phase === 'triggering') {
      abandon(attempt);
    }
  }

  private cancelTracked(attempt: Attempt): void {
    stopPolling(attempt);
    this.setState({ phase: 'cancelling' });
    const self = this;
    this.api.cancel(attempt.bundleLuid, attempt.backgroundJobId).then(function() {
      if (self.isLive(attempt)) {
        self.setState({ phase: 'cancelled' });
      }
    }, function() {
      if (!self.isLive(attempt)) {
        return;
      }
      // The cancel didn't go through (e.g. the job finished first): show what actually happened.
      self.onError('cancel');
      self.api.getStatus(attempt.backgroundJobId).then(function(execution) {
        if (self.isLive(attempt)) {
          self.applyStatus(attempt, execution);
        }
      }, function() {
        if (self.isLive(attempt)) {
          self.setState({ phase: 'statusUnavailable' });
        }
      });
    });
  }

  private poll(attempt: Attempt): void {
    const self = this;
    this.api.getStatus(attempt.backgroundJobId).then(function(execution) {
      if (!self.isTracking(attempt)) {
        return;
      }
      attempt.pollFailures = 0;
      self.applyStatus(attempt, execution);
    }, function() {
      if (!self.isTracking(attempt)) {
        return;
      }
      attempt.pollFailures++;
      if (attempt.pollFailures >= MAX_CONSECUTIVE_POLL_FAILURES || trackingTimedOut(attempt)) {
        self.setState({ phase: 'statusUnavailable' });
        return;
      }
      // A network blip or 5xx doesn't mean the upload failed: back off and try again.
      self.schedulePoll(attempt, Math.min(MAX_POLL_BACKOFF_MS, POLL_INTERVAL_MS * Math.pow(2, attempt.pollFailures)));
    });
  }

  private applyStatus(attempt: Attempt, execution: IExecution): void {
    const stage: BundleUploadStage = toUploadStage(execution);
    if (isTerminalStage(stage)) {
      this.setState({ phase: TERMINAL_PHASES[stage], execution: execution });
      return;
    }
    this.setState({ phase: 'tracking', execution: execution });
    if (trackingTimedOut(attempt)) {
      this.setState({ phase: 'statusUnavailable' });
      return;
    }
    this.schedulePoll(attempt, POLL_INTERVAL_MS);
  }

  private schedulePoll(attempt: Attempt, delayMs: number): void {
    const self = this;
    stopPolling(attempt);
    attempt.pollTimer = window.setTimeout(function() {
      attempt.pollTimer = undefined;
      self.poll(attempt);
    }, delayMs);
  }

  /** Releases what an attempt that won't be tracked holds: its job (if triggered) or its reservation. */
  private release(attempt: Attempt): void {
    if (attempt.released || !attempt.bundleLuid) {
      return;
    }
    attempt.released = true;
    if (attempt.backgroundJobId) {
      // The trigger landed after the cancel, so the upload task exists: cancel it by job.
      const onError = this.onError;
      this.api.cancel(attempt.bundleLuid, attempt.backgroundJobId).catch(function() { onError('cancel'); });
    } else {
      // Best effort: an orphaned reservation is cleaned up server-side.
      this.api.cancel(attempt.bundleLuid).catch(function() { /* ignored */ });
    }
  }

  /** The attempt is current and still allowed to update the dialog. */
  private isLive(attempt: Attempt): boolean {
    return !this.disposed && attempt === this.attempt && !attempt.abandoned;
  }

  private isTracking(attempt: Attempt): boolean {
    return this.isLive(attempt) && this.state.phase === 'tracking';
  }

  private isBusy(): boolean {
    const phase = this.state.phase;
    return phase === 'uploading' || phase === 'triggering' || phase === 'tracking' || phase === 'cancelling';
  }

  private setState(partial: Partial<BundleUploadState>): void {
    this.state = { ...this.state, ...partial };
    if (!this.disposed) {
      this.onChange(this.state);
    }
  }
}

function throwIfAbandoned(attempt: Attempt): void {
  if (attempt.abandoned) {
    throw ABANDONED;
  }
}

function abandon(attempt: Attempt): void {
  attempt.abandoned = true;
  attempt.cancelToken.cancel();
  stopPolling(attempt);
}

function stopPolling(attempt: Attempt): void {
  if (attempt.pollTimer !== undefined) {
    window.clearTimeout(attempt.pollTimer);
    attempt.pollTimer = undefined;
  }
}

function trackingTimedOut(attempt: Attempt): boolean {
  return attempt.trackingStartedAtMs !== undefined && Date.now() - attempt.trackingStartedAtMs > MAX_TRACKING_MS;
}
